import { ComponentType, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { usePermissions } from "@/hooks/use-permissions";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import {
  RESOURCE_VIEWS,
  ResourceViewKind,
  resolverAcessoVisao,
} from "@/components/routing/resource-views";
import { OutrasVisoesPanel } from "./OutrasVisoesPanel";
import { withPermissionPage } from "./WithPermissionPage";

/**
 * Guarda de rota para recursos que a mesma pessoa pode abrir por mais de uma
 * área. Link de outra área não vira mais 403 na cara do usuário: se ele tem
 * uma única visão do recurso, cai nela; se tem várias, escolhe; se não tem
 * nenhuma, vê o "Acesso Restrito" padrão.
 */
export function withResourceView<P extends object>(
  Component: ComponentType<P>,
  recurso: ResourceViewKind,
  viewKey: string,
) {
  const views = RESOURCE_VIEWS[recurso];
  const atual = views.find((view) => view.key === viewKey);

  if (!atual) throw new Error(`Visão "${viewKey}" não existe no catálogo de "${recurso}"`);

  const PaginaComPermissao = withPermissionPage(Component, [atual.permissao]);

  return function ResourceViewGuard(props: P) {
    const params = useParams();
    const { hasPermission } = usePermissions();
    const { goTo } = useNavigationHelpers();

    // produção gráfica declara a rota como :planilhaId; as demais, como :id
    const recursoId = params.id ?? params.planilhaId;

    const acesso = recursoId
      ? resolverAcessoVisao(views, viewKey, hasPermission)
      : ({ tipo: "permitida" } as const);

    const destino = acesso.tipo === "redirecionar" ? acesso.destino : undefined;
    const destinoLabel = destino?.label;

    useEffect(() => {
      if (destinoLabel) {
        toast.info(`Você não tem acesso à visão de ${atual.label}. Abrimos em ${destinoLabel}.`);
      }
    }, [destinoLabel]);

    if (destino) return <Navigate to={destino.path(recursoId!)} replace />;

    if (acesso.tipo === "escolher") {
      return (
        <OutrasVisoesPanel
          visaoLabel={atual.label}
          alternativas={acesso.alternativas}
          onAbrir={(view) => goTo(view.path(recursoId!))}
        />
      );
    }

    // 'permitida' segue para a página; 'bloqueada' cai no painel de Acesso Restrito
    return <PaginaComPermissao {...props} />;
  };
}
